import { Box, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import type { ReactNode } from 'react'
import { gradientBrand } from '../theme/brand'

export type PageHeaderProps = {
  title: string
  subtitle?: string
  icon?: ReactNode
  actions?: ReactNode
  mb?: number | { xs: number; sm: number }
  titleComponent?: 'h1' | 'h2' | 'div'
}

export function PageHeader({
  title,
  subtitle,
  icon,
  actions,
  mb = { xs: 2, sm: 3 },
  titleComponent = 'h1',
}: PageHeaderProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        mb,
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minWidth: 0, flex: '1 1 auto' }}>
        {icon ? (
          <Box
            sx={(t) => ({
              flexShrink: 0,
              width: 52,
              height: 52,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.contrastText',
              background: gradientBrand,
              boxShadow: `0 6px 20px ${alpha(t.palette.primary.main, 0.28)}`,
              '& .MuiSvgIcon-root': { fontSize: 28 },
            })}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            component={titleComponent}
            fontWeight={700}
            color="primary.main"
            gutterBottom={Boolean(subtitle)}
            sx={{ fontSize: { xs: '1.45rem', sm: '2rem' }, lineHeight: 1.25 }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.65 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {actions ? (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            alignItems: 'center',
            alignSelf: { xs: 'stretch', sm: 'center' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            '& .MuiButton-root': {
              minHeight: 38,
              px: 1.75,
              fontWeight: 600,
            },
            '& .MuiButton-startIcon': {
              mr: 0.75,
            },
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  )
}
